import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApplyOverride } from "@/hooks/useOverrides";
import { applyOverride } from "@/services/overridesApi";
import type { OverrideApplyResponse, VariantOverride } from "@/types/overrides";

interface ApplyOverrideDialogProps {
  override: VariantOverride | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Failed to apply override";
}

function noMatchMessage(reason: string | null): string {
  return reason ? `No match: ${reason}` : "No match:";
}

function confirmFailureMessage(error: unknown): string {
  return `${errorMessage(error)}. A timeout may still have applied the override on the server.`;
}

export function ApplyOverrideDialog({
  override,
  open,
  onOpenChange,
}: ApplyOverrideDialogProps) {
  const { mutateAsync, isPending } = useApplyOverride();
  const [preview, setPreview] = useState<OverrideApplyResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [previewNonce, setPreviewNonce] = useState(0);

  const overrideId = override?.id;
  const overrideIdRef = useRef(overrideId);
  overrideIdRef.current = overrideId;

  useEffect(() => {
    setConfirmError(null);
  }, [open, overrideId]);

  useEffect(() => {
    if (!open || overrideId == null) {
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);

    applyOverride(overrideId, true)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setPreviewError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, overrideId, previewNonce]);

  if (!override) return null;

  const hasMatch =
    (preview?.applied.length ?? 0) > 0 && !preview?.no_match_reason;
  const confirmDisabled =
    previewLoading || isPending || !hasMatch || previewError != null;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isPending) return;
    onOpenChange(nextOpen);
  };

  const handleConfirm = async () => {
    const submittedId = override.id;
    try {
      const result = await mutateAsync({ id: submittedId, dryRun: false });
      if (overrideIdRef.current !== submittedId) return;
      if (result.applied.length === 0) {
        setPreview(result);
        setConfirmError(noMatchMessage(result.no_match_reason));
        return;
      }
      const count = result.applied.length;
      toast.success(
        count === 1
          ? "Applied override to 1 tube"
          : `Applied override to ${count} tubes`,
      );
      onOpenChange(false);
    } catch (err) {
      if (overrideIdRef.current !== submittedId) return;
      const message = confirmFailureMessage(err);
      toast.error(message);
      setConfirmError(message);
      setPreviewNonce((n) => n + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Apply Override</DialogTitle>
          <DialogDescription>
            Review the changes below. Confirm applies them. Preview of{" "}
            <span className="font-medium text-foreground">
              {override.field_name}
            </span>{" "}
            on variant{" "}
            <span className="font-medium text-foreground">
              {override.external_variant_id}
            </span>{" "}
            ({override.source}): {override.match_value} →{" "}
            {override.override_value}
          </DialogDescription>
        </DialogHeader>

        {previewLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            Previewing apply...
          </div>
        )}

        {previewError && (
          <p className="text-sm text-destructive" role="alert">
            {previewError}
          </p>
        )}

        {confirmError && (
          <p className="text-sm text-destructive" role="alert">
            {confirmError}
          </p>
        )}

        {!previewLoading && preview && (
          <div className="space-y-4">
            {preview.no_match_reason || preview.applied.length === 0 ? (
              !confirmError && (
                <p className="text-sm text-muted-foreground">
                  {noMatchMessage(preview.no_match_reason)}
                </p>
              )
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Variant IDs</TableHead>
                      <TableHead>Inventory</TableHead>
                      <TableHead>Tube Position</TableHead>
                      <TableHead>Old → New</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.applied.map((row) => (
                      <TableRow key={`${row.inventory_id}-${row.tube_id}`}>
                        <TableCell className="font-mono text-sm">
                          {row.variant_ids.join(", ")}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {row.inventory_id}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {row.tube_position ?? (
                            <span className="italic">any</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          <span className="text-destructive">
                            {row.old_value}
                          </span>
                          <span className="text-muted-foreground mx-1">→</span>
                          <span className="text-green-600 dark:text-green-400">
                            {row.new_value}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {hasMatch && (
              <div className="text-sm text-muted-foreground space-y-1">
                <p>
                  {preview.stale_review_variant_ids.length} review
                  {preview.stale_review_variant_ids.length === 1
                    ? ""
                    : "s"}{" "}
                  will be marked stale
                </p>
                <p>
                  {preview.resolved_issue_ids.length} issue
                  {preview.resolved_issue_ids.length === 1 ? "" : "s"} will be
                  resolved
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={confirmDisabled}>
            {isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
